// Command palette — modal with fuzzy search of every interface plus a
// few action commands. Triggered by Cmd+K (Mac) or Ctrl+K (Win/Linux)
// from anywhere in the dashboard. Wired into Dashboard.js via outlet
// context so child routes can also open it.
//
// Arrow keys move selection, Enter activates, Esc closes.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  InputAdornment,
  Box,
  Typography,
  Stack,
  Chip,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import { usePermissions } from '../context/PermissionContext';

// Same map as DashboardHome — interface keys per category landing path.
const CATEGORY_INTERFACES = {
  '/dashboard/pdf-tools': [
    'ama9999-ucc-labels', 'amazon-di-ucc-label', 'bilingual-labels', 'carton-labels',
    'expense-allocation-invoices', 'home-goods-pdf', 'pdf-manipulation', 'pdf-reports',
    'pdf-to-excel-ucc-labels', 'pallet-labels', 'pdq-full-pallet-pull-label', 'cad-invoices',
    'master-retail-rugs', 'label-printing',
  ],
  '/dashboard/excel-tools': [
    'excel-export-item-images', 'items-detail', 'po-distribution', 'po-distribution-whs',
    'sales-orders-export', 'excel-reports',
  ],
  '/dashboard/link-tools': [
    'aws-file-links', 'aws-file-links-alternate', 'amazon-bulk-image-uploader',
    'broken-link-checker', 'asset-explorer', 'bulk-image-downloader',
  ],
  '/dashboard/other-tools': [
    '48-state-freight', 'customer-style-search', 'document-translation', 'marketing-copies',
    'product-details', 'remove-background', 'price-scraper', 'social-media-content',
    'product-data-validator', 'email-campaign-builder', 'barcode-generator', 'file-converter',
  ],
  '/dashboard/ai-tools': ['ai-image-generator', 'ai-video-generator', 'seo-content-writer'],
  '/dashboard/analytics-tools': ['sales-dashboard', 'sales-dashboard-v2', 'returns-dashboard', 'new-items', 'customer-style-gaps', 'collection-launches', 'sales-pulse', 'reorder-console'],
};

const CATEGORY_LABELS = {
  '/dashboard/pdf-tools': 'PDF',
  '/dashboard/excel-tools': 'Excel',
  '/dashboard/link-tools': 'Link',
  '/dashboard/other-tools': 'Other',
  '/dashboard/ai-tools': 'AI',
  '/dashboard/analytics-tools': 'Analytics',
};

const niceName = (slug) => slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// Simple fuzzy match: every char in `q` must appear in `s` in order. Score
// rewards shorter strings + earlier matches. Returns null when no match.
const fuzzyMatch = (q, s) => {
  if (!q) return 0;
  const qq = q.toLowerCase();
  const ss = s.toLowerCase();
  let qi = 0, si = 0, score = 0, lastMatch = -1;
  while (qi < qq.length && si < ss.length) {
    if (qq[qi] === ss[si]) {
      // Reward consecutive matches and earlier positions
      score += (lastMatch === si - 1 ? 5 : 1) + Math.max(0, 10 - si);
      lastMatch = si;
      qi++;
    }
    si++;
  }
  if (qi < qq.length) return null;  // not all chars matched
  return score - ss.length * 0.05;  // slight penalty for length
};

export default function CommandPalette({ open, onClose, actions }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAdmin, isOwner, hasAccessToPath } = usePermissions();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const isDark = theme.palette.mode === 'dark';

  // Build the searchable index — every accessible interface + the action
  // commands the dashboard exposes (theme toggle, sign out, etc.).
  const allItems = useMemo(() => {
    const items = [];
    for (const [catPath, slugs] of Object.entries(CATEGORY_INTERFACES)) {
      const catLabel = CATEGORY_LABELS[catPath];
      // Add the category landing itself
      items.push({
        kind: 'category',
        label: catLabel + ' Tools',
        category: catLabel,
        path: catPath,
      });
      for (const slug of slugs) {
        if (!isAdmin && !isOwner && !hasAccessToPath(slug)) continue;
        items.push({
          kind: 'interface',
          label: niceName(slug),
          category: catLabel,
          path: '/dashboard/' + slug,
          slug,
        });
      }
    }
    // Pinned action commands
    for (const a of (actions || [])) items.push({ kind: 'action', ...a });
    return items;
  }, [isAdmin, isOwner, hasAccessToPath, actions]);

  // Run the fuzzy filter on every change of the query.
  const results = useMemo(() => {
    if (!query.trim()) return allItems.slice(0, 30);
    return allItems
      .map(it => ({ it, score: fuzzyMatch(query, `${it.label} ${it.category}`) }))
      .filter(r => r.score !== null)
      .sort((a, b) => b.score - a.score)
      .slice(0, 30)
      .map(r => r.it);
  }, [query, allItems]);

  // Reset selection when results change.
  useEffect(() => { setSelectedIndex(0); }, [query]);

  // Focus the input when the dialog opens.
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      // Tiny delay so the dialog finishes mounting before focus.
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  // Scroll the active item into view when arrow keys move selection.
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-cmd-idx="${selectedIndex}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const activate = (item) => {
    if (!item) return;
    onClose();
    if (item.kind === 'action' && typeof item.run === 'function') item.run();
    else if (item.path) navigate(item.path);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      activate(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          mt: 6,  // dock toward the top, not centered vertically
          alignSelf: 'flex-start',
        },
      }}
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
    >
      {/* Search input */}
      <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, px: 2, py: 1.5 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          variant="standard"
          placeholder="Search tools or commands…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={onClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
            sx: { fontSize: '1rem', fontWeight: 500 },
          }}
        />
      </Box>

      <DialogContent sx={{ p: 0, maxHeight: 420 }} ref={listRef}>
        {results.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No results for "{query}"
            </Typography>
          </Box>
        ) : (
          <Box sx={{ py: 0.5 }}>
            {results.map((item, idx) => (
              <Box
                key={item.path || item.id || idx}
                data-cmd-idx={idx}
                onClick={() => activate(item)}
                onMouseEnter={() => setSelectedIndex(idx)}
                sx={{
                  px: 2, py: 1.25, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  bgcolor: selectedIndex === idx
                    ? (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)')
                    : 'transparent',
                  borderLeft: selectedIndex === idx
                    ? `3px solid ${theme.palette.primary.main}`
                    : '3px solid transparent',
                  transition: 'background-color 120ms, border-color 120ms',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.label}
                  </Typography>
                  {item.category && (
                    <Typography variant="caption" color="text.secondary">
                      {item.category}
                    </Typography>
                  )}
                  {item.kind === 'action' && item.hint && (
                    <Typography variant="caption" color="text.secondary">{item.hint}</Typography>
                  )}
                </Box>
                <Chip
                  size="small"
                  label={item.kind === 'action' ? 'COMMAND' : item.kind === 'category' ? 'CATEGORY' : 'TOOL'}
                  sx={{
                    height: 20, fontSize: 9, fontWeight: 700, letterSpacing: 0.5,
                    bgcolor: item.kind === 'action'
                      ? '#FFC10720'
                      : item.kind === 'category' ? `${theme.palette.primary.main}20` : 'transparent',
                    color: item.kind === 'action' ? '#F9A825' : 'text.secondary',
                    border: item.kind === 'interface' ? `1px solid ${theme.palette.divider}` : 'none',
                  }}
                />
                {selectedIndex === idx && (
                  <KeyboardReturnIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      {/* Footer hint strip */}
      <Box sx={{
        px: 2, py: 1, borderTop: `1px solid ${theme.palette.divider}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
        bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
      }}>
        <Stack direction="row" spacing={1.5}>
          <Typography variant="caption" color="text.secondary">
            <strong>↑↓</strong> Navigate
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <strong>↵</strong> Open
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <strong>Esc</strong> Close
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {results.length} result{results.length === 1 ? '' : 's'}
        </Typography>
      </Box>
    </Dialog>
  );
}
